import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { PatientDataContext } from '../../../context/PatientDataContext';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { ChevronRightIcon, ChevronLeftIcon } from '@heroicons/react/solid';
import Breadcrumb from '../../../components/Breadcrumb';
import CISideNavbar from './components/CISideNavbar';
// import Select from 'react-select';
// import Creatable from 'react-select/creatable';
import AutoSaveCreatable from '../../../components/AutoSaveCreatable';
import DictationTextArea from '../../../components/DictationTextArea';
import InfoTooltip from '../../../components/InfoTooltip';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import { TriggerGenerateDocumentSection } from '../../../utils/TriggerGenerateDocumentSection';
import { auth } from '../../../firebase';
import { isClinicianAssignedToStage } from '../../../utils/clinicianPermissions';
import { useAuth } from '../../../hooks/useAuth';
import useWorkflowConfig from '../../../hooks/useWorkflowConfig';
import { isCISectionExcludedByIdentity, buildCIExclusionsContext, getVisibleCISections, mapCIDisplayTitle } from '../../../utils/clinicalInterviewWorkflow';
import { useSectionExitRegeneration } from '../../../hooks/useSectionExitRegeneration';
import { buildMergedSelection, splitPredefinedAndCustom } from '../../../utils/selectionMergeUtils';

// Clinician guidance for selected MSE areas. Owned here (not on the patient
// document) so every existing patient sees the approved copy immediately.
// Section tooltips are keyed by subsectionVarName; title is a fallback for
// older documents that may not have varName. titleDisplay includes "10.x"
// and must not be used as a key — numbering can differ by workflow.
const MSE_HEADER_TOOLTIP =
  "The MSE is a snapshot of the patient's mental state during this assessment. It complements the clinical history. Use neutral, descriptive language and avoid interpreting intent. Consider whether each feature is typical or atypical for the patient in the context of the assessment, and use the details field to select from the drop-down options or describe in your own words any clinically relevant findings. The MSE should reflect your clinical assessment and observations, and does not replace a fuller assessment of risk, diagnosis, history or functioning where these are clinically indicated.";

// Stable aria-label — do not interpolate displaySectionTitle. Tests mock
// mapCIDisplayTitle to the US "Status" form; production shows UK "State".
const MSE_HEADER_TOOLTIP_LABEL = 'Information about the Mental State Examination';

const MSE_SECTION_TOOLTIPS_BY_VAR = {
  speech: 'Observe their mechanics of speech.',
  affect: "This refers to the clinician's direct observation of the patient's emotional expression during the assessment. Unlike Mood (what the patient says), Affect is what the clinician sees in the moment.",
  thoughtForm: 'Focus on their flow and structure of thinking.',
  thoughtContent: 'Focus on the themes.',
};

const MSE_SECTION_TOOLTIPS_BY_TITLE = {
  Speech: MSE_SECTION_TOOLTIPS_BY_VAR.speech,
  Affect: MSE_SECTION_TOOLTIPS_BY_VAR.affect,
  'Thought Form': MSE_SECTION_TOOLTIPS_BY_VAR.thoughtForm,
  'Thought Content': MSE_SECTION_TOOLTIPS_BY_VAR.thoughtContent,
};

const MSE_MOOD_INTRO =
  'Record their current mood at the time of the assessment, using their own words.';

// Supporting prompts sit under the main instruction as bullets so the
// suggested question and the history pointer scan as separate points.
const MSE_MOOD_BULLETS = [
  'Suggested question: "How would you describe your mood today?"',
  'Longer term patterns should be explored in the clinical history.',
];

function getMseSectionTooltip(subsection) {
  return (
    MSE_SECTION_TOOLTIPS_BY_VAR[subsection.subsectionVarName] ||
    MSE_SECTION_TOOLTIPS_BY_TITLE[subsection.title]
  );
}

function isMoodSubsection(subsection) {
  return subsection.subsectionVarName === 'mood' || subsection.title === 'Mood';
}

// "?" sits beside the heading, not inside it, so the heading name stays
// exactly what it is. Same markup contract as CIQuestionPanel: tooltip
// first, then the heading element as a sibling.
function MseHeadingWithTooltip({ tooltip, label, maxWidth = '300px', children }) {
  return (
    <div className="flex items-center gap-2">
      {tooltip ? <InfoTooltip content={tooltip} label={label} maxWidth={maxWidth} /> : null}
      {children}
    </div>
  );
}

export default function MentalStatusExamination() {

  const { patientId } = useParams();
  const navigate = useNavigate();
  const { patientDataModel, updatePatientFirestore, flushPendingUpdates } = useContext(PatientDataContext);
  const { claims } = useAuth();
  const wf = useWorkflowConfig(patientDataModel, claims);

  const currentSectionNumber = 10;
  const currentStageTitle = 'Clinical Interview';
  const currentSectionTitle = 'Mental Status Examination';
  const displaySectionTitle = mapCIDisplayTitle('Mental Status Examination');
  const prevSectionLink = '/proforma/clinical-interview/psychiatric-history';
  const prevSectionTitle = 'Psychiatric History';
  const nextSectionLink = '/proforma/clinical-interview/risk-assessment';
  const nextSectionTitle = 'Risk Assessment';

  const [canEdit, setCanEdit] = useState(false);

  // Route guard: if this organisation/patient excludes MSE, skip this page entirely
  useEffect(() => {
    const organisationName = claims?.organisation || 'default';
    const isExcluded = isCISectionExcludedByIdentity(
      patientDataModel,
      organisationName,
      'Mental Status Examination',
      'mentalStatusExamination'
    );
    if (isExcluded) {
      navigate(`/patient/${patientId}${nextSectionLink}`, { replace: true });
    }
  }, [patientDataModel, claims, navigate, patientId, nextSectionLink]);

  // Check if the current clinician is assigned to this stage
  useEffect(() => {
    const checkPermissions = async () => {
      const currentUser = auth.currentUser;
      if (currentUser && patientDataModel) {
        const hasEditPermission = isClinicianAssignedToStage(patientDataModel, currentUser.uid, 'CI');
        setCanEdit(hasEditPermission);
      }
    };
    
    checkPermissions();
  }, [patientDataModel]);

  const organisationName = claims?.organisation || 'default';
  const { sections: ciSections, excludedKeys } = buildCIExclusionsContext(patientDataModel, organisationName);
  const SECTIONS_CI = useMemo(() => getVisibleCISections(ciSections, excludedKeys), [ciSections, excludedKeys]);
  const currentSectionIndex = useMemo(() => SECTIONS_CI.findIndex(s => s.key === '10'), [SECTIONS_CI]);

  // Get all subsections from patient data model
  const mseSubsections = useMemo(() => {
    const sectionData = patientDataModel?.dataModel?.['2_clinicalInterview']?.sections?.[currentSectionNumber];
    if (!sectionData || !sectionData.subsections) return [];

    // Convert subsections object to sorted array
    return Object.entries(sectionData.subsections)
      .sort((a, b) => parseInt(a[0]) - parseInt(b[0])) // Sort by subsection number
      .map(([key, subsection]) => ({
        key,
        ...subsection
      }));
  }, [patientDataModel, currentSectionNumber]);


  const [initialized, setInitialized] = useState(false);
  const [freeTextValues, setFreeTextValues] = useState({});
  const [clickthroughSelections, setClickthroughSelections] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);

  const { markRegenHandled } = useSectionExitRegeneration({
    patientId,
    stageTitle: currentStageTitle,
    sectionTitle: currentSectionTitle,
    hasChanges,
    canEdit,
    patientDataModel,
  });

  // ---------- Confirmation dialog for toggle changes ----------
  // Guards against accidentally clearing dropdown + sub-category selections
  // when a clinician toggles the Yes/No (Typical/Atypical) button.
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingToggle, setPendingToggle] = useState(null);

  // Returns true when toggling the Yes/No button would lose subcategory
  // selections that cannot be automatically preserved.  Custom (free-text)
  // dropdown entries are always preserved by buildMergedSelection, so they
  // no longer trigger the confirmation dialog.  Only subcategory trees
  // attached to predefined parent options need guarding — when those parent
  // options are swapped out, their subcategory data is lost.
  const hasSelectionsToLose = useCallback((panelIndex, categoryName) => {
    const subSelections = clickthroughSelections[panelIndex]?.[categoryName]?.subSelections || {};
    return Object.values(subSelections).some(
      (opts) => (opts || []).length > 0
    );
  }, [clickthroughSelections]);

  const handleConfirmToggle = () => {
    if (!pendingToggle) return;

    if (pendingToggle.type === 'single') {
      executeSelection(pendingToggle.panelIndex, pendingToggle.categoryName, pendingToggle.isAppropriate);
    } else if (pendingToggle.type === 'yesToAll') {
      executeYesToAll(pendingToggle.panelIndex);
    }

    setPendingToggle(null);
    setConfirmDialogOpen(false);
  };

  // Add this state to store the additional notes value
const [additionalNotes, setAdditionalNotes] = useState('');


  // Initialize selections from patient data
  useEffect(() => {
    if (!initialized && patientDataModel?.dataModel) {
      const sectionData = patientDataModel?.dataModel?.['2_clinicalInterview']?.sections?.[currentSectionNumber];
      if (!sectionData || !sectionData.subsections) return;

      // Initialize freeText values
      const textValues = {};
      
      // Initialize clickthrough selections
      const selections = [];

      // First, determine the maximum subsection key to properly size our array
      const maxSubsectionKey = Math.max(...Object.keys(sectionData.subsections).map(key => parseInt(key)));
      // Pre-fill the array with null values up to the maximum subsection key
      for (let i = 0; i <= maxSubsectionKey; i++) {
        selections.push(null);
      }
            
      Object.entries(sectionData.subsections)
        .sort((a, b) => parseInt(a[0]) - parseInt(b[0]))
        .forEach(([subSectionKey, subsection]) => {
          if (subsection.subsectionType === 'freeText') {
            // For free text subsections, get values from clinicianNotes
            Object.keys(subsection.categories || {}).forEach(categoryName => {
              textValues[categoryName] = sectionData.clinicianNotes?.[categoryName] || '';
            });
          } else if (subsection.subsectionType === 'mseClickthrough') {
            // For clickthrough subsections, initialize selections
            const categoriesData = subsection.categories || {};
            const categorySelections = Object.entries(categoriesData).reduce((acc, [categoryName, category]) => {
              acc[categoryName] = {
                appropriate: category.isAppropriateSelection ?? null,
                selectedOption: (category.selectionDetails || []).map(option => ({ value: option, label: option })),
                subSelections: {}
              };

              // Initialize subcategory selections if any
              if (category.subcategories) {
                Object.entries(category.subcategories).forEach(([optionName, subCategoryObj]) => {
                  Object.entries(subCategoryObj).forEach(([subCategoryName, subCategoryData]) => {
                    if (!acc[categoryName].subSelections) {
                      acc[categoryName].subSelections = {};
                    }
                    acc[categoryName].subSelections[subCategoryName] = 
                      (subCategoryData.selectionDetails || []).map(option => ({ value: option, label: option }));
                  });
                });
              }
              
              return acc;
            }, {});
            
            // Store selections at the correct index position matching the subsection key
          selections[parseInt(subSectionKey) - 1] = categorySelections;
        }
      });
      
      // Initialize additional notes
      setAdditionalNotes(sectionData.clinicianNotes?.['Additional Notes'] || '');
    
      setFreeTextValues(textValues);
      setClickthroughSelections(selections);
      setInitialized(true);
    }
  }, [patientDataModel, currentSectionNumber, initialized]);

  const sendSectionUpdates = useCallback((newUpdates) => {
    if (!canEdit) {
      console.warn('Update blocked: Clinician does not have edit permission for this stage');
      return Promise.resolve();
    }

    return updatePatientFirestore(patientId, {
      '2_clinicalInterview': {
        sections: {
          [currentSectionNumber]: newUpdates
        }
      }
    });
  }, [patientId, currentSectionNumber, updatePatientFirestore, canEdit]);

  // Handle free text input changes
  const handleTextChange = (categoryName, value) => {
    if (!canEdit) return;

    setFreeTextValues(prev => ({
      ...prev,
      [categoryName]: value
    }));
    setHasChanges(true);
  };

  // Handle free text blur events
  const handleTextBlur = (categoryName, value) => {
    if (!canEdit) return;

    sendSectionUpdates({
      clinicianNotes: {
        [categoryName]: value
      }
    });
  };

  // Add these handler functions for the additional notes
const handleAdditionalNotesChange = (value) => {
  if (!canEdit) return;

  setAdditionalNotes(value);
  setHasChanges(true);
};

const handleAdditionalNotesBlur = () => {
  if (!canEdit) return;

  sendSectionUpdates({
    clinicianNotes: {
      'Additional Notes': additionalNotes
    }
  });
};

  // Core toggle logic extracted so it can be called directly (no data at risk)
  // or after the user confirms through the alert dialog.  Uses the functional
  // updater for setClickthroughSelections to avoid mutating state in-place.
  //
  // When toggling, predefined dropdown options (from yesOptions/noOptions) are
  // replaced with the appropriate default, but any custom (clinician-typed)
  // free-text entries are preserved via buildMergedSelection.  Subcategories
  // tied to removed predefined parent options are cleared; subcategories for
  // retained values (custom entries or the new default) are kept.
  const executeSelection = (panelIndex, categoryName, isAppropriate) => {
    const subsection = mseSubsections[panelIndex];
    const category = subsection.categories[categoryName];
    const defaultValue = isAppropriate ? (category.yesOptions?.[0] || null) : null;
    const existingSelections = clickthroughSelections[panelIndex]?.[categoryName]?.selectedOption || [];

    const newSelectedOption = buildMergedSelection(
      existingSelections, category.yesOptions || [], category.noOptions || [], defaultValue
    );

    // Keep subcategories only for values that survived the merge (the new
    // default plus any custom entries).  Subcategories for removed predefined
    // parent options are dropped.
    const retainedValues = new Set(newSelectedOption.map(o => o.value));
    const existingSubs = clickthroughSelections[panelIndex]?.[categoryName]?.subSelections || {};
    const filteredSubs = {};
    Object.entries(existingSubs).forEach(([subCatName, opts]) => {
      const parentOption = subCatName.split(' - ')[0];
      if (retainedValues.has(parentOption)) {
        filteredSubs[subCatName] = opts;
      }
    });

    setClickthroughSelections(prev => {
      const newSelections = [...prev];
      newSelections[panelIndex] = {
        ...(newSelections[panelIndex] || {}),
        [categoryName]: {
          appropriate: isAppropriate,
          selectedOption: newSelectedOption,
          subSelections: filteredSubs
        }
      };
      return newSelections;
    });
    setHasChanges(true);

    sendSectionUpdates({
      subsections: {
        [subsection.key]: {
          categories: {
            [categoryName]: {
              isAppropriateSelection: isAppropriate,
              selectionDetails: newSelectedOption.map(o => o.value)
            }
          }
        }
      }
    });
  };

  // Guarded handler — shows confirmation dialog when a button click would
  // discard existing dropdown / sub-category selections.  This applies both
  // when toggling between Yes↔No AND when making a first selection on a
  // category whose dropdown already has values (e.g. custom-typed entries
  // added before any button was pressed).
  const handleSelection = (panelIndex, categoryName, isAppropriate) => {
    if (!canEdit) return;

    const currentAppropriate = clickthroughSelections[panelIndex]?.[categoryName]?.appropriate;

    // Clicking the already-active button means "unselect" — reset to null.
    // Otherwise keep the new boolean value for a normal select or switch.
    const newValue = currentAppropriate === isAppropriate ? null : isAppropriate;

    // If there are dropdown/sub-category values that would be cleared, confirm first.
    // This applies equally to switching between Yes↔No AND to unselecting.
    if (hasSelectionsToLose(panelIndex, categoryName)) {
      setPendingToggle({ type: 'single', panelIndex, categoryName, isAppropriate: newValue });
      setConfirmDialogOpen(true);
      return;
    }

    executeSelection(panelIndex, categoryName, newValue);
  };
  
  // Handle dropdown selection changes.  Uses functional updater to build
  // new state objects instead of mutating existing ones in-place.
  const handleOptionChange = (panelIndex, categoryName, selectedOptions) => {
    if (!canEdit) return;

    setClickthroughSelections(prev => {
      const newSelections = [...prev];
      const existingPanel = newSelections[panelIndex] || {};
      const existingCategory = existingPanel[categoryName] || {
        appropriate: null, selectedOption: [], subSelections: {}
      };

      // Keep only sub-category selections whose parent option is still selected
      const selectedValues = new Set(selectedOptions.map(o => o.value));
      const filteredSubs = {};
      Object.entries(existingCategory.subSelections || {}).forEach(([subCatName, opts]) => {
        if (selectedValues.has(subCatName.split(' - ')[0])) {
          filteredSubs[subCatName] = opts;
        }
      });

      newSelections[panelIndex] = {
        ...existingPanel,
        [categoryName]: {
          ...existingCategory,
          selectedOption: selectedOptions,
          subSelections: filteredSubs
        }
      };
      return newSelections;
    });
    setHasChanges(true);

    sendSectionUpdates({
      subsections: {
        [mseSubsections[panelIndex].key]: {
          categories: {
            [categoryName]: {
              selectionDetails: selectedOptions.map(o => o.value)
            }
          }
        }
      }
    });
  };

  // Handle subcategory dropdown selection changes.  Uses functional updater
  // to construct new state objects instead of mutating nested references.
  const handleSubcategoryChange = (panelIndex, categoryName, optionValue, subCategoryName, selectedOptions) => {
    if (!canEdit) return;

    setClickthroughSelections(prev => {
      const newSelections = [...prev];
      const existingPanel = newSelections[panelIndex] || {};
      const existingCategory = existingPanel[categoryName] || {
        appropriate: null, selectedOption: [], subSelections: {}
      };

      newSelections[panelIndex] = {
        ...existingPanel,
        [categoryName]: {
          ...existingCategory,
          subSelections: {
            ...(existingCategory.subSelections || {}),
            [subCategoryName]: selectedOptions
          }
        }
      };
      return newSelections;
    });
    setHasChanges(true);

    sendSectionUpdates({
      subsections: {
        [mseSubsections[panelIndex].key]: {
          categories: {
            [categoryName]: {
              subcategories: {
                [optionValue]: {
                  [subCategoryName]: {
                    selectionDetails: selectedOptions.map(o => o.value)
                  }
                }
              }
            }
          }
        }
      }
    });
  };

  // Core "Yes to all" logic, extracted for the confirm-then-execute pattern.
  // Uses functional updater to build a fresh panel object instead of mutating.
  // Custom (clinician-typed) entries are preserved for each category via
  // buildMergedSelection; only predefined options are replaced with the Yes default.
  const executeYesToAll = (panelIndex) => {
    const subsection = mseSubsections[panelIndex];
    const sortedCategories = Object.entries(subsection.categories || {})
      .sort((a, b) => a[1].categoryNumber - b[1].categoryNumber);

    setClickthroughSelections(prev => {
      const newSelections = [...prev];
      const newPanel = { ...(newSelections[panelIndex] || {}) };

      sortedCategories.forEach(([categoryName, category]) => {
        const defaultValue = category.yesOptions?.[0] || null;
        const existingSelections = newPanel[categoryName]?.selectedOption || [];
        const merged = buildMergedSelection(
          existingSelections, category.yesOptions || [], category.noOptions || [], defaultValue
        );
        newPanel[categoryName] = {
          appropriate: true,
          selectedOption: merged,
          subSelections: {}
        };
      });

      newSelections[panelIndex] = newPanel;
      return newSelections;
    });
    setHasChanges(true);

    const categoriesUpdates = {};
    sortedCategories.forEach(([categoryName, category]) => {
      const defaultValue = category.yesOptions?.[0] || null;
      const existingSelections = clickthroughSelections[panelIndex]?.[categoryName]?.selectedOption || [];
      const merged = buildMergedSelection(
        existingSelections, category.yesOptions || [], category.noOptions || [], defaultValue
      );
      categoriesUpdates[categoryName] = {
        isAppropriateSelection: true,
        selectionDetails: merged.map(o => o.value)
      };
    });

    sendSectionUpdates({
      subsections: {
        [subsection.key]: {
          categories: categoriesUpdates
        }
      }
    });
  };

  // Guarded "Yes to all" — confirms if any category has data that would be lost.
  const handleYesToAll = (panelIndex) => {
    if (!canEdit) return;

    const subsection = mseSubsections[panelIndex];
    const sortedCategories = Object.entries(subsection.categories || {})
      .sort((a, b) => a[1].categoryNumber - b[1].categoryNumber);

    const anyMeaningful = sortedCategories.some(
      ([categoryName]) => hasSelectionsToLose(panelIndex, categoryName)
    );

    if (anyMeaningful) {
      setPendingToggle({ type: 'yesToAll', panelIndex });
      setConfirmDialogOpen(true);
      return;
    }

    executeYesToAll(panelIndex);
  };

  const handleSectionCompletion = useCallback((patientId, currentStageTitle, currentSectionTitle, hasChanges) => {
    if (!canEdit) return Promise.resolve();

    const currentSectionData = patientDataModel?.dataModel?.['2_clinicalInterview']
      ?.sections?.[currentSectionNumber];
    const isAlreadyCompleted = currentSectionData?.status === 'completed';

    const sectionsAlreadyGenerated = patientDataModel?.dataModel?.['6_manageReports']?.sections?.['1']?.stageSectionsGenerated;
    const isCurrentSectionGenerated = sectionsAlreadyGenerated?.includes(currentSectionTitle) || false;

    const needsStatusWrite = !isAlreadyCompleted;
    const needsGeneration = hasChanges || !isCurrentSectionGenerated;

    if (!needsStatusWrite && !needsGeneration) {
      return Promise.resolve();
    }

    if (needsStatusWrite) {
      sendSectionUpdates({ status: 'completed', progress: 100 });
    }

    const dataReady = flushPendingUpdates({ noTimeout: true });

    if (needsGeneration) {
      TriggerGenerateDocumentSection(patientId, currentStageTitle, currentSectionTitle, [], dataReady);
    }

    return dataReady;
  }, [sendSectionUpdates, patientDataModel, canEdit, flushPendingUpdates, currentSectionNumber]);

  const handleNextSection = useCallback(() => {
    markRegenHandled();
    handleSectionCompletion(patientId, currentStageTitle, currentSectionTitle, hasChanges);
    navigate(`/patient/${patientId}${nextSectionLink}`);
  }, [patientId, navigate, nextSectionLink, hasChanges, handleSectionCompletion, markRegenHandled]);

  const handlePrevSection = useCallback(() => {
    markRegenHandled();
    handleSectionCompletion(patientId, currentStageTitle, currentSectionTitle, hasChanges);
    navigate(`/patient/${patientId}${prevSectionLink}`);
  }, [patientId, navigate, prevSectionLink, hasChanges, handleSectionCompletion, markRegenHandled]);

  const navigateToSection = useCallback((index) => {
    markRegenHandled();
    handleSectionCompletion(patientId, currentStageTitle, currentSectionTitle, hasChanges);
    const section = SECTIONS_CI[index];
    if (section.route) {
      navigate(`/patient/${patientId}${section.route}`);
    }
  }, [patientId, navigate, SECTIONS_CI, hasChanges, handleSectionCompletion, markRegenHandled]);

  const breadcrumbTuples = useMemo(() => [
    
    ["Patient Overview", `/patient/${patientId}`],
    [currentStageTitle],
    [displaySectionTitle]
  ], [patientId, currentStageTitle, displaySectionTitle]);

  const styles = useMemo(() => ({
    menu: (base) => ({
      ...base,
      textAlign: 'left',
    }),
    option: (base, state) => ({
      ...base,
      textAlign: 'left',
      backgroundColor: state.isSelected ? 'lightgray' : base.backgroundColor,
    }),
  }), []);

  // Render a free text subsection
  const renderFreeTextSubsection = (subsection, index) => {
    return (
      <div key={subsection.key} className="bg-gray-50 rounded-2xl border border-gray-200 text-left p-6 shadow-md mb-6">
        <h3 className="font-bold text-lg mb-4">{subsection.titleDisplay}</h3>
        {isMoodSubsection(subsection) ? (
          <div className="text-left text-gray-700 mb-4">
            <p>{MSE_MOOD_INTRO}</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              {MSE_MOOD_BULLETS.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="text-left text-gray-700 mb-4">
            {subsection.tooltipText}
          </p>
        )}
        
        {Object.keys(subsection.categories || {}).map((categoryName) => (
          <div key={`${subsection.key}-${categoryName}`} className="flex flex-col">
            <div className="flex gap-1 mb-2">
              <h4 className="text-gray-700 text-sm font-semibold">
                {categoryName}
              </h4>
            </div>
            
            <DictationTextArea
              value={freeTextValues[categoryName] || ''}
              onChange={(val) => handleTextChange(categoryName, val)}
              onBlur={(e) => handleTextBlur(categoryName, e.target.value)}
              canEdit={canEdit}
              ariaLabel={`${categoryName} notes`}
            />
          </div>
        ))}
      </div>
    );
  };

  // Render a clickthrough subsection
  const renderClickthroughSubsection = (subsection, index) => {
    // Sort categories by categoryNumber
    const sortedCategories = Object.entries(subsection.categories || {})
      .sort((a, b) => a[1].categoryNumber - b[1].categoryNumber);

    // Disable the bulk action when every category in this panel is already Yes
    const allAlreadyYes = sortedCategories.length > 0 && sortedCategories.every(
      ([catName]) => clickthroughSelections[index]?.[catName]?.appropriate === true
    );
    const yesToAllDisabled = !canEdit || allAlreadyYes;

    return (
      <div key={subsection.key} className="bg-gray-50 rounded-2xl border border-gray-200 text-left p-6 shadow-md mb-6">
        <div className="flex justify-between items-center mb-4">
          <div className="w-1/3">
            <MseHeadingWithTooltip
              tooltip={getMseSectionTooltip(subsection)}
              label={subsection.title ? `Information about ${subsection.title}` : undefined}
            >
              <h4 className="font-bold text-lg">{subsection.titleDisplay}</h4>
            </MseHeadingWithTooltip>
          </div>
          <div className="w-1/6 flex justify-center">
            <button
              onClick={() => handleYesToAll(index)}
              className={`py-2 px-4 rounded-md shadow-md font-bold text-black bg-white border border-black transition-all duration-100 ${
                        yesToAllDisabled
                          ? 'opacity-60 cursor-not-allowed'
                          : 'hover:bg-green-700 hover:text-white'
                      }`}
                      disabled={yesToAllDisabled}
            >
              Yes to all?
            </button>
          </div>
          <div className="w-1/2"></div>
        </div>
        <table className="w-full table-auto">
          <thead>
            <tr>
              <th className="text-left font-bold text-base w-1/3">Category</th>
              <th className="text-center font-bold text-base w-1/6">Typical?</th>
              <th className="text-center font-bold text-base w-1/2">Details</th>
            </tr>
          </thead>
          <tbody>
            {sortedCategories.map(([categoryName, category], catIndex) => (
              <React.Fragment key={`${subsection.key}-${categoryName}`}>
                <tr className="border-t">
                  <td className="py-2 font-bold text-sm">{categoryName}</td>
                  <td className="py-2 text-center">
                    <button
                      onClick={() => handleSelection(index, categoryName, true)}
                      className={`w-16 py-1 rounded-l-full border font-bold transition-all duration-300 ${clickthroughSelections[index]?.[categoryName]?.appropriate === true
                        ? 'bg-green-200 border-gray-600 border-2'
                        : 'bg-white border-gray-400'
                        }`}
                    >
                      Yes
                    </button>
                    <button
                      onClick={() => handleSelection(index, categoryName, false)}
                      className={`w-16 py-1 rounded-r-full border font-bold transition-all duration-300 ${clickthroughSelections[index]?.[categoryName]?.appropriate === false
                        ? 'bg-red-200 border-gray-600 border-2'
                        : 'bg-white border-gray-400'
                        }`}
                    >
                      No
                    </button>
                  </td>
                  <td className="py-2 text-center">
                    <AutoSaveCreatable
                      isMulti
                      isDisabled={!canEdit}
                      styles={styles}
                      options={(clickthroughSelections[index]?.[categoryName]?.appropriate 
                        ? category.yesOptions 
                        : category.noOptions)?.map(option => ({ value: option, label: option })) || []}
                      value={clickthroughSelections[index]?.[categoryName]?.selectedOption || []}
                      onChange={(selectedOptions) => handleOptionChange(index, categoryName, selectedOptions)}
                      formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
                      placeholder={canEdit ? "Select options or write your own.." : "Selection can only be made by assigned clinician"}                            
                      className="basic-multi-select text-sm"
                      classNamePrefix="select"                      
                    />
                  </td>
                </tr>
                
                {/* Render subcategory dropdowns */}
                {category.subcategories && clickthroughSelections[index]?.[categoryName]?.selectedOption?.some(option => 
                  category.subcategories[option.value]) && (
                  clickthroughSelections[index]?.[categoryName]?.selectedOption.map(option => (
                    category.subcategories[option.value] && Object.entries(category.subcategories[option.value]).map(([subCategoryName, subCategory]) => (
                      <tr key={`${subsection.key}-${categoryName}-${option.value}-${subCategoryName}`} className="border-t">
                        <td className="py-2 pl-8 font-bold text-sm">{option.value} - {subCategoryName}</td>
                        <td colSpan="2" className="py-2 text-center">
                          <AutoSaveCreatable
                            isMulti
                            isDisabled={!canEdit}
                            styles={styles}
                            options={(subCategory?.options || []).map(option => ({ value: option, label: option }))}
                            value={clickthroughSelections[index]?.[categoryName]?.subSelections?.[subCategoryName] || []}
                            onChange={(selectedOptions) => 
                              handleSubcategoryChange(index, categoryName, option.value, subCategoryName, selectedOptions)
                            }
                            formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
                            placeholder={canEdit ? "Select options or write your own.." : "Selection can only be made by assigned clinician"}                            
                            className="basic-multi-select text-sm"
                            classNamePrefix="select"
                          />
                        </td>
                      </tr>
                    ))
                  ))
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderNotesPanel = () => {
    return (
      <div className="bg-gray-50 rounded-2xl border border-gray-200 text-left p-6 shadow-md mb-6">
        <h3 className="font-bold text-lg mb-4">Additional Notes</h3>
        <p className="text-left text-gray-700 mb-4">
          Please add any additional comments, observations or relevant information not covered in the sections above.
        </p>
        
        <div className="flex flex-col">
          <div className="flex gap-1 mb-2">
            <h4 className="text-gray-700 text-sm font-semibold">
              Clinical Notes
            </h4>
          </div>
          
          <DictationTextArea
            value={additionalNotes}
            onChange={(val) => handleAdditionalNotesChange(val)}
            onBlur={handleAdditionalNotesBlur}
            canEdit={canEdit}
            ariaLabel="Additional clinical notes"
          />
        </div>
      </div>
    );
  };


  return (
    <Layout activePage="dashboard">
      <div className="flex justify-between items-center">
        <Breadcrumb tupleList={breadcrumbTuples} />
      </div>
      <div className="flex">
        <CISideNavbar
          sections={SECTIONS_CI}
          currentSectionIndex={currentSectionIndex}
          navigateToSection={navigateToSection}
          wf={wf}
        />
        <div className="flex-grow p-8">
          <div className="flex justify-between items-center mb-6">
            <button
              onClick={handlePrevSection}
              className="flex items-center py-2 px-4 mb-2 rounded-md shadow-md text-gray-900 border border-gray-300 transition-all duration-300 hover:bg-gray-50"
              aria-label="Back to Previous Section"
            >
              <ChevronLeftIcon className="w-5 h-5 mr-2" aria-hidden="true" />
              <span className="font-bold">{`Back to ${prevSectionTitle}`}</span>
            </button>
            <button
              onClick={handleNextSection}
              className="flex items-center py-2 px-4 mb-2 rounded-md shadow-md transition duration-100 ml-auto hover:bg-violet-800 bg-violet-900 text-white"
            >
              <span className="font-bold">{`Go to ${nextSectionTitle}`}</span>
              <ChevronRightIcon className="w-5 h-5 ml-2" aria-hidden="true" />
            </button>
          </div>
          <div className="flex justify-between items-center mb-6">
            <MseHeadingWithTooltip
              tooltip={MSE_HEADER_TOOLTIP}
              label={MSE_HEADER_TOOLTIP_LABEL}
              maxWidth="420px"
            >
              <h2 className="text-2xl font-bold">{currentSectionNumber}. {displaySectionTitle}</h2>
            </MseHeadingWithTooltip>
          </div>
          
          {/* Render each subsection based on its type */}
          {mseSubsections.map((subsection, index) => 
            subsection.subsectionType === 'freeText' 
              ? renderFreeTextSubsection(subsection, index)
              : renderClickthroughSubsection(subsection, index)
          )}

          {renderNotesPanel()}

          <div className="flex justify-end items-center mt-8 pt-4 border-t border-gray-200">                          
            <button
              onClick={handleNextSection}
              className="flex items-center py-2 px-4 mb-2 rounded-md shadow-md transition duration-100 ml-auto hover:bg-violet-800 bg-violet-900 text-white"
            >
              <span className="font-bold">{`Go to ${nextSectionTitle}`}</span>
              <ChevronRightIcon className="w-5 h-5 ml-2" aria-hidden="true" />
            </button>
          </div>
        </div>
      

      </div>

      {/* Confirmation dialog shown when toggling Yes/No would discard
          existing dropdown or sub-category selections. */}
      <AlertDialog.Root open={confirmDialogOpen} onOpenChange={(open) => { setConfirmDialogOpen(open); if (!open) setPendingToggle(null); }}>
        <AlertDialog.Portal>
          <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/50 z-50" />
          <AlertDialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg p-6 w-[420px] max-w-[90vw] z-50 shadow-xl">
            <AlertDialog.Title className="text-lg font-bold mb-2">
              Confirm Selection Change
            </AlertDialog.Title>
            <AlertDialog.Description className="text-sm text-gray-600 mb-5">
              {pendingToggle?.type === 'yesToAll'
                ? 'Setting all categories to "Yes" will reset sub-category selections to their defaults. Your custom notes will be kept.'
                : 'Changing this selection will reset the sub-category details for this category. Your custom notes will be kept.'}
            </AlertDialog.Description>
            <div className="flex justify-end gap-3">
              <AlertDialog.Cancel asChild>
                <button className="py-2 px-4 font-semibold border border-gray-300 rounded-md hover:bg-gray-50 transition-colors">
                  Cancel
                </button>
              </AlertDialog.Cancel>
              <AlertDialog.Action asChild>
                <button
                  onClick={handleConfirmToggle}
                  className="py-2 px-4 font-semibold rounded-md bg-violet-900 text-white hover:bg-violet-800 transition-colors"
                >
                  Continue
                </button>
              </AlertDialog.Action>
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    </Layout>
  );
}
